import Reveal from "@/components/site/Reveal";
import PageHero from "@/components/site/PageHero";
import LeadForm from "@/components/site/LeadForm";
import Seo, { ORG_SCHEMA, breadcrumbSchema } from "@/components/site/Seo";

export default function About() {
  return (
    <div className="bg-white">
      <Seo
        title="About — British-Owned UAE Consultancy"
        description="British-owned UAE consultancy offering personal, expert guidance on Golden Visas, residency, business setup and relocation across the Emirates."
        keywords="about Velor Advisory, British consultancy UAE, Dubai relocation advisors, UAE immigration consultancy, Chloe Lavender Velor"
        focusKeyword="British consultancy UAE"
        path="/about"
        schema={[
          ORG_SCHEMA,
          {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "@id": "https://velor.ae/about#aboutpage",
            url: "https://velor.ae/about",
            name: "About Velor Advisory",
            description:
              "British-owned UAE consultancy offering personal, expert guidance on Golden Visas, residency, business setup and relocation.",
            mainEntity: { "@id": "https://velor.ae/#organization" },
            inLanguage: "en-GB",
          },
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "About", path: "/about" },
          ]),
        ]}
      />
      <PageHero
        overline="About Velor Advisory"
        title="Trusted Advisors for Relocation, Residency and Business in the UAE."
        subtitle="Founded on the belief that relocating to the UAE should be a seamless transition, supported by expert guidance at every stage."
        image="https://images.pexels.com/photos/2044434/pexels-photo-2044434.jpeg?auto=compress&cs=tinysrgb&w=1650&fm=webp"
        imageAlt="Two British advisors reviewing UAE relocation and Golden Visa documents together at a modern office"
      />

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-black/45">Our story</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-[1.05]">Why we started <em className="italic">Velor</em>.</h2>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7 text-black/70 font-light text-lg leading-relaxed space-y-6">
            <p>Velor was founded by British professionals who chose to make the UAE their home. Through our own experiences, we came to appreciate both the opportunities the region offers and the practical challenges that often arise when relocating or establishing a presence here.</p>
            <p>We believe these decisions deserve thoughtful guidance and personal attention. Whether someone is moving with their family, launching a new venture or exploring residency options, our role is to provide clarity, confidence and support throughout the process.</p>
            <p>Today, we are privileged to help clients from across the UK, Europe and beyond navigate their transition to life and business in the UAE.</p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-black/10 bg-[#fafafa] py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-12">
          {[
            { n: "01", t: "Discretion", d: "Your enquiry is treated as confidential from the very first message." },
            { n: "02", t: "Senior advisory", d: "No call centres. No handoffs. One advisor, end-to-end." },
            { n: "03", t: "Transparent pricing", d: "Fixed-fee, written proposals — clear, complete, and signed off in advance." },
          ].map((v, i) => (
            <Reveal key={v.t} delay={i * 100}>
              <div className="text-[11px] tracking-overline uppercase text-black/45">{v.n}</div>
              <h3 className="font-serif text-3xl mt-4">{v.t}</h3>
              <p className="mt-3 text-black/60 font-light leading-relaxed">{v.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="text-[11px] tracking-overline uppercase text-black/45">Get in touch</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">Begin a private conversation.</h2>
            <p className="mt-6 text-black/60 font-light max-w-md">Tell us a little about you and we'll arrange a discreet consultation at a time that suits.</p>
          </Reveal>
          <Reveal delay={120} className="md:col-span-7">
            <LeadForm testid="about-lead-form" source="about" />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
